
import React from 'react';
import { Skill } from '../../types/game';
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Lock, Check, ChevronRight } from "lucide-react";
import { skills as allSkills } from '../../data/skills';

interface SkillItemProps {
  skill: Skill;
  status: 'acquired' | 'locked' | 'insufficient-exp' | 'available';
  hasSkill: (skillId: string) => boolean;
  experience: number;
  animationDelay: number;
}

const SkillItem: React.FC<SkillItemProps> = ({
  skill,
  status,
  hasSkill,
  experience,
  animationDelay
}) => {
  return (
    <motion.div 
      data-skill-id={skill.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: animationDelay }}
      className={`border p-4 rounded-md shadow ${
        status === 'acquired' 
          ? 'bg-web3-green/10 border-web3-green shadow-web3-green/5' 
          : status === 'locked'
          ? 'bg-muted/30 border-muted'
          : status === 'insufficient-exp'
          ? 'bg-yellow-500/10 border-yellow-500/30'
          : 'bg-web3-blue/10 border-web3-blue/30 hover:border-web3-blue/50 hover:-translate-y-1 transition-all'
      }`}
    >
      <div className="flex justify-between items-start">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <h4 className="font-medium text-white flex items-center">
                {status === 'locked' && <Lock className="h-4 w-4 mr-2 text-muted-foreground" />}
                {status === 'acquired' && <Check className="h-4 w-4 mr-2 text-web3-green" />}
                {skill.name}
                {skill.prerequisites && skill.prerequisites.length > 0 && (
                  <ChevronRight className="h-4 w-4 ml-1 text-muted-foreground" />
                )}
              </h4>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-card/90 backdrop-blur-md border-white/10 p-3 max-w-xs">
              <p className="font-medium mb-1">{skill.name}</p>
              <p className="text-xs">{skill.description}</p>
              {skill.prerequisites && skill.prerequisites.length > 0 && (
                <div className="mt-2 text-xs">
                  <span className="text-muted-foreground">Requires: </span>
                  {skill.prerequisites.map((prereqId, i) => {
                    const prereqSkill = allSkills.find(s => s.id === prereqId);
                    return (
                      <span key={prereqId} className={hasSkill(prereqId) ? 'text-web3-green' : 'text-muted-foreground'}>
                        {prereqSkill?.name || prereqId}
                        {i < skill.prerequisites!.length - 1 ? ', ' : ''}
                      </span>
                    );
                  })}
                </div>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        {status === 'acquired' && (
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ 
              type: "spring",
              stiffness: 260,
              damping: 20 
            }}
          >
            <Badge className="bg-web3-green text-white">
              Acquired
            </Badge>
          </motion.div>
        )}
        
        {status === 'available' && (
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ 
              type: "spring",
              stiffness: 260,
              damping: 20 
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Badge className="bg-web3-blue text-white">
              Available
            </Badge>
          </motion.div>
        )}
        
        {status === 'insufficient-exp' && (
          <Badge variant="outline" className="border-yellow-500 text-yellow-500">
            Needs XP: {skill.requiredExperience}
          </Badge>
        )}
        
        {status === 'locked' && (
          <Badge variant="outline" className="border-muted-foreground text-muted-foreground">
            Locked
          </Badge>
        )}
      </div>
      
      <p className="text-sm mt-2 text-white/80">{skill.description}</p>
      
      <SkillPrerequisites skill={skill} hasSkill={hasSkill} />
      
      {skill.requiredExperience && status !== 'acquired' && (
        <SkillExperienceBar 
          experience={experience} 
          requiredExperience={skill.requiredExperience} 
        />
      )}
    </motion.div>
  );
};

interface SkillPrerequisitesProps {
  skill: Skill;
  hasSkill: (skillId: string) => boolean;
}

const SkillPrerequisites: React.FC<SkillPrerequisitesProps> = ({ skill, hasSkill }) => {
  if (!skill.prerequisites || skill.prerequisites.length === 0) {
    return null;
  }
  
  return (
    <div className="mt-3 p-2 bg-black/20 rounded text-xs backdrop-blur-sm">
      <span className="text-muted-foreground">Prerequisites: </span>
      {skill.prerequisites.map((prereqId, i) => {
        const prereqSkill = allSkills.find(s => s.id === prereqId);
        const hasPrereq = hasSkill(prereqId);
        
        return (
          <span key={prereqId} className={hasPrereq ? 'text-web3-green' : 'text-muted-foreground'}>
            {prereqSkill?.name || prereqId}
            {i < skill.prerequisites!.length - 1 ? ', ' : ''}
          </span>
        );
      })}
    </div>
  );
};

interface SkillExperienceBarProps {
  experience: number;
  requiredExperience: number;
}

const SkillExperienceBar: React.FC<SkillExperienceBarProps> = ({ 
  experience, 
  requiredExperience 
}) => {
  return (
    <div className="mt-2 flex items-center justify-between">
      <div className="text-xs text-muted-foreground">
        XP Required: {requiredExperience}
      </div>
      <div className="w-1/2 h-1 bg-muted/30 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-web3-blue to-web3-green" 
          style={{ width: `${Math.min(100, (experience / requiredExperience) * 100)}%` }}
        ></div>
      </div>
    </div>
  );
};

export default SkillItem;
